import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, Loader2, Sparkles, Check } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { AssetImage } from '@/types/asset';

interface ImageGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentImage?: string | AssetImage;
  onImageSelect: (image: string | AssetImage) => void;
  title?: string;
}

interface ImageModel {
  id: string;
  name: string;
  description: string;
  provider: string;
}

export function ImageGenerationDialog({
  open,
  onOpenChange,
  currentImage,
  onImageSelect,
  title = 'Workflow Image',
}: ImageGenerationDialogProps) {
  const [activeTab, setActiveTab] = useState<'url' | 'generate'>('url');
  const [models, setModels] = useState<ImageModel[]>([]);

  // URL Tab state
  const [lightUrlInput, setLightUrlInput] = useState('');
  const [darkUrlInput, setDarkUrlInput] = useState('');

  // Generate Tab state
  const [selectedModel, setSelectedModel] = useState('');
  const [masterPrompt, setMasterPrompt] = useState('');
  const [lightModifier, setLightModifier] = useState('bright background, light theme');
  const [darkModifier, setDarkModifier] = useState('dark background, dark theme');
  const [style, setStyle] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [steps, setSteps] = useState(20);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [runwareModelId, setRunwareModelId] = useState('');
  const [useNanoBanana, setUseNanoBanana] = useState(false);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [lightImageUrl, setLightImageUrl] = useState('');
  const [darkImageUrl, setDarkImageUrl] = useState('');
  const [lightThumbnailUrl, setLightThumbnailUrl] = useState('');
  const [darkThumbnailUrl, setDarkThumbnailUrl] = useState('');
  const [deleteOnRegenerate, setDeleteOnRegenerate] = useState(true);

  // Load models on mount
  useEffect(() => {
    if (open) {
      loadModels();
      // Pre-fill from current image if it's an AssetImage
      if (currentImage && typeof currentImage === 'object') {
        setMasterPrompt(currentImage.masterPrompt || '');
        setLightModifier(currentImage.light?.modifier || 'bright background, light theme');
        setDarkModifier(currentImage.dark?.modifier || 'dark background, dark theme');
        setStyle(currentImage.style || '');
        setSelectedModel(currentImage.model || '');
        setLightImageUrl(currentImage.light?.url || '');
        setDarkImageUrl(currentImage.dark?.url || '');
        // Also pre-fill URL inputs
        setLightUrlInput(currentImage.light?.url || '');
        setDarkUrlInput(currentImage.dark?.url || '');
      } else if (currentImage && typeof currentImage === 'string') {
        // Legacy string URL - set as light URL
        setLightUrlInput(currentImage);
      }
    }
  }, [open, currentImage]);

  // Auto-select first model
  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0]?.id || '');
    }
  }, [models, selectedModel]);

  const loadModels = async () => {
    try {
      const response = await adminApi.getImageModels();
      setModels(response.models);
    } catch (err: any) {
      setError('Failed to load image models: ' + (err.message || 'Unknown error'));
    }
  };

  const handleGenerateImages = async () => {
    if (!masterPrompt.trim()) {
      setError('Please enter a master prompt');
      return;
    }

    if (!selectedModel) {
      setError('Please select a model');
      return;
    }

    setIsGenerating(true);
    setError('');

    // Delete old images if checkbox is checked and images exist
    if (deleteOnRegenerate && (lightImageUrl || darkImageUrl)) {
      try {
        if (lightImageUrl) {
          await adminApi.deleteImage(lightImageUrl);
        }
        if (darkImageUrl) {
          await adminApi.deleteImage(darkImageUrl);
        }
      } catch (err: any) {
        console.warn('Failed to delete old images:', err);
        // Continue with generation even if delete fails
      }
    }

    setLightImageUrl('');
    setDarkImageUrl('');
    setLightThumbnailUrl('');
    setDarkThumbnailUrl('');

    try {
      // Generate light theme image
      const lightResult = await adminApi.generateImage({
        prompt: `${masterPrompt}${style ? `, ${style}` : ''}${lightModifier ? `, ${lightModifier}` : ''}`,
        model: selectedModel,
        width,
        height,
        steps,
        ...(negativePrompt && { negativePrompt }),
        ...(runwareModelId && { runwareModelId }),
      });

      setLightImageUrl(lightResult.url);
      setLightThumbnailUrl(lightResult.thumbnailUrl || '');

      // Generate dark theme image
      if (useNanoBanana) {
        // Use nano banana (google:4@1) for dark mode generation from light image
        const darkResult = await adminApi.generateImage({
          prompt: `${masterPrompt}${style ? `, ${style}` : ''}${darkModifier ? `, ${darkModifier}` : ''}`,
          model: 'google:4@1', // Force nano banana model
          inputImage: lightResult.url, // Use light image as input
          runwareModelId: 'google:4@1', // Override to ensure nano banana is used
        });

        setDarkImageUrl(darkResult.url);
        setDarkThumbnailUrl(darkResult.thumbnailUrl || '');
      } else {
        // Generate dark theme image independently
        const darkResult = await adminApi.generateImage({
          prompt: `${masterPrompt}${style ? `, ${style}` : ''}${darkModifier ? `, ${darkModifier}` : ''}`,
          model: selectedModel,
          width,
          height,
          steps,
          ...(negativePrompt && { negativePrompt }),
          ...(runwareModelId && { runwareModelId }),
        });

        setDarkImageUrl(darkResult.url);
        setDarkThumbnailUrl(darkResult.thumbnailUrl || '');
      }
    } catch (err: any) {
      setError('Failed to generate images: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyUrl = () => {
    // Check if at least one URL is provided
    if (!lightUrlInput.trim() && !darkUrlInput.trim()) {
      setError('Please enter at least one image URL');
      return;
    }

    // If both URLs provided, create AssetImage
    if (lightUrlInput.trim() && darkUrlInput.trim()) {
      const assetImage: AssetImage = {
        masterPrompt: 'Manual URL entry',
        light: {
          url: lightUrlInput.trim(),
        },
        dark: {
          url: darkUrlInput.trim(),
        },
      };
      console.log('[ImageGenerationDialog] Applying URL AssetImage:', assetImage);
      onImageSelect(assetImage);
    } else {
      // If only one URL, use as string (legacy format)
      const singleUrl = lightUrlInput.trim() || darkUrlInput.trim();
      console.log('[ImageGenerationDialog] Applying single URL:', singleUrl);
      onImageSelect(singleUrl);
    }

    onOpenChange(false);
  };

  const handleApplyGenerated = () => {
    if (!lightImageUrl || !darkImageUrl) {
      setError('Please generate images first');
      return;
    }

    const assetImage: AssetImage = {
      masterPrompt,
      light: {
        url: lightImageUrl,
        thumbnailUrl: lightThumbnailUrl || undefined,
        modifier: lightModifier || undefined,
      },
      dark: {
        url: darkImageUrl,
        thumbnailUrl: darkThumbnailUrl || undefined,
        modifier: darkModifier || undefined,
      },
      style: style || undefined,
      model: selectedModel,
    };

    console.log('[ImageGenerationDialog] Applying generated AssetImage:', assetImage);
    onImageSelect(assetImage);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Enter an image URL or generate themed images with AI
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="url">Enter URL</TabsTrigger>
            <TabsTrigger value="generate">Generate Images</TabsTrigger>
          </TabsList>

          {/* URL Tab */}
          <TabsContent value="url" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lightImageUrl">Light Theme Image URL</Label>
              <Input
                id="lightImageUrl"
                value={lightUrlInput}
                onChange={(e) => setLightUrlInput(e.target.value)}
                placeholder="https://example.com/light-image.png"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="darkImageUrl">Dark Theme Image URL</Label>
              <Input
                id="darkImageUrl"
                value={darkUrlInput}
                onChange={(e) => setDarkUrlInput(e.target.value)}
                placeholder="https://example.com/dark-image.png"
              />
              <p className="text-xs text-muted-foreground">
                Provide both URLs for themed images, or just one for a single image
              </p>
            </div>

            {(lightUrlInput || darkUrlInput) && (
              <div className="space-y-4">
                <Label>Preview</Label>
                <div className="grid grid-cols-2 gap-4">
                  {lightUrlInput && (
                    <div className="space-y-2">
                      <Label className="text-xs">Light Theme</Label>
                      <div className="border rounded-lg overflow-hidden">
                        <img src={lightUrlInput} alt="Light preview" className="w-full h-auto" onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="18" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                        }} />
                      </div>
                    </div>
                  )}
                  {darkUrlInput && (
                    <div className="space-y-2">
                      <Label className="text-xs">Dark Theme</Label>
                      <div className="border rounded-lg overflow-hidden bg-gray-900 p-2">
                        <img src={darkUrlInput} alt="Dark preview" className="w-full h-auto" onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23333"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="18" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Button onClick={handleApplyUrl} className="w-full">
              Apply Image URLs
            </Button>
          </TabsContent>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger id="model">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="masterPrompt">Master Prompt *</Label>
              <Textarea
                id="masterPrompt"
                value={masterPrompt}
                onChange={(e) => setMasterPrompt(e.target.value)}
                className="min-h-[80px]"
                placeholder="Describe the image (theme-agnostic)..."
              />
              <p className="text-xs text-muted-foreground">
                Base description of the image, without theme-specific details
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lightModifier">Light Theme Modifier</Label>
                <Input
                  id="lightModifier"
                  value={lightModifier}
                  onChange={(e) => setLightModifier(e.target.value)}
                  placeholder="bright background, light theme"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="darkModifier">Dark Theme Modifier</Label>
                <Input
                  id="darkModifier"
                  value={darkModifier}
                  onChange={(e) => setDarkModifier(e.target.value)}
                  placeholder="dark background, dark theme"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="style">Style (Optional)</Label>
              <Input
                id="style"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="minimalist, illustrated, photorealistic..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="negativePrompt">Negative Prompt (Optional)</Label>
              <Textarea
                id="negativePrompt"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="blurry, low quality, distorted, text, watermark..."
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Things you don't want in the generated image
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="genWidth">Width</Label>
                <Input
                  id="genWidth"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(parseInt(e.target.value) || 1024)}
                  min={256}
                  max={2048}
                  step={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="genHeight">Height</Label>
                <Input
                  id="genHeight"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(parseInt(e.target.value) || 1024)}
                  min={256}
                  max={2048}
                  step={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="genSteps">Steps</Label>
                <Input
                  id="genSteps"
                  type="number"
                  value={steps}
                  onChange={(e) => setSteps(parseInt(e.target.value) || 20)}
                  min={1}
                  max={100}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="runwareModelId">Runware Model ID (Optional - for experimentation)</Label>
              <Input
                id="runwareModelId"
                value={runwareModelId}
                onChange={(e) => setRunwareModelId(e.target.value)}
                placeholder="e.g., google:4@1, runware:100@1"
              />
              <p className="text-xs text-muted-foreground">
                Override the Runware model ID for testing different models
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="useNanoBanana"
                checked={useNanoBanana}
                onCheckedChange={(checked) => setUseNanoBanana(checked as boolean)}
              />
              <Label
                htmlFor="useNanoBanana"
                className="text-sm font-normal cursor-pointer"
              >
                Use Nano Banana for dark mode (generate from light image)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="deleteOnRegenerate"
                checked={deleteOnRegenerate}
                onCheckedChange={(checked) => setDeleteOnRegenerate(checked as boolean)}
              />
              <Label
                htmlFor="deleteOnRegenerate"
                className="text-sm font-normal cursor-pointer"
              >
                Delete previous images when regenerating
              </Label>
            </div>

            <Button
              onClick={handleGenerateImages}
              disabled={isGenerating || !selectedModel}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Both Images...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Light + Dark Images
                </>
              )}
            </Button>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {(lightImageUrl || darkImageUrl) && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {lightImageUrl && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>Light Theme</Label>
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <img src={lightImageUrl} alt="Light theme" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                  {darkImageUrl && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>Dark Theme</Label>
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="border rounded-lg overflow-hidden bg-gray-900 p-2">
                        <img src={darkImageUrl} alt="Dark theme" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>

                <Button onClick={handleApplyGenerated} className="w-full" size="lg">
                  Apply Themed Images
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
